/**
 * ERPs Invariants
 */

import { CommandOperationMap, Invariants, gateCommandByOperation } from '@commandDrivenStateEngine/core/invariants';
import { ERPsState, DEFAULT_DRIFT_FILTER } from './state';
import { ERPsCommand } from './commands';

/**
 * Command operations — the AddERPModal "connect" click flips
 * `loading/setSubmitting` ( false → true ) from the `user` source, which the
 * `erpsSubmitPolicy` reacts to by calling `ProvisionSystemIntegration`.
 * Capability is DERIVED from the named client method.
 *
 * `drift/recheck/request` (TV1-2146) is gated the same way: the drift policy
 * answers it by calling `backend.post.erp_drift_recheck`, so the capability the
 * operator needs is whatever that operation requires
 * (`systems-integrations:erp-drift-recheck` → `sales-channels:integrations:configure`),
 * derived rather than hand-typed.
 *
 * THIS ENTRY AND THE `user`-BRANCH ALLOW BELOW ARE ONE ATOMIC PAIR (decisions.md
 * D11). `gateCommandByOperation` returns TRUE for a command ABSENT from this map,
 * so allowing the command in `canApply` without an entry here would ship a
 * privileged, external-system-reaching command COMPLETELY UNGATED. Never remove
 * one without the other.
 */
export const commandOperations: CommandOperationMap<ERPsState, ERPsCommand> = {
  'integrations/erps/loading/setSubmitting': 'ProvisionSystemIntegration',
  'integrations/erps/drift/recheck/request': 'erp_drift_recheck'
};

export const invariants: Invariants<ERPsState, ERPsCommand> = {
  canApply: ({ state, command, meta }) => {
    const { type } = command;
    const { source } = meta;

    // API source
    if ( source === 'api' ) {
      return (
        type.startsWith( 'integrations/erps/data/' ) ||
        type.startsWith( 'integrations/erps/loading/' ) ||
        type === 'integrations/erps/addModal/close' ||
        type === 'integrations/erps/delete/close' ||
        type === 'integrations/erps/detail/close' ||
        type.startsWith( 'integrations/erps/oauth/' ) ||
        // Drift audit (TV1-2146): the policy owns load / join / paging / settle.
        // `recheck/request` is excluded — it is an operator act that reaches an
        // external system, so it may only originate from the `user` source.
        ( type.startsWith( 'integrations/erps/drift/' ) &&
          type !== 'integrations/erps/drift/recheck/request' )
      );
    }

    // AI source
    if ( source === 'ai' ) {
      if (
        type === 'integrations/erps/addModal/submit' ||
        type === 'integrations/erps/delete/confirm' ||
        type === 'integrations/erps/detail/save'
      ) {
        return false;
      }
      if ( type.startsWith( 'integrations/erps/data/' )) return false;
      // Drift re-check calls out to the ERP. Denied for `ai` for the same reason
      // `addModal/submit` and `delete/confirm` are: an AI-sourced command must
      // never reach an external system on the operator's behalf. The read-shaped
      // drift commands ( setFilter / setPage ) stay allowed by the fallthrough.
      if ( type === 'integrations/erps/drift/recheck/request' ) return false;
      return true;
    }

    // URL source — opening the detail for the `:erpId` in the path, and
    // restoring the `?tab=` selection on a refresh.
    if ( source === 'url' ) {
      // No drift command is URL-sourced: the drift panel's filter and page are
      // not represented in the URL, so accepting them here would be a gate with
      // no caller.
      return (
        type === 'integrations/erps/detail/open' ||
        type === 'integrations/erps/detail/setTab'
      );
    }

    // User source — privileged provision trigger gated by commandOperations.
    if ( source === 'user' ) {
      if ( type.startsWith( 'integrations/erps/data/' )) return false;
      // Drift audit: only the operator-initiated commands. The rest of the drift
      // slice is policy-owned, so a `user` dispatch of e.g. `dataLoaded` would be a
      // bug rather than an interaction.
      //
      // `recheck/request` IS allowed here — and it is allowed ONLY because it now
      // carries a `commandOperations` entry (see above), which is what makes the
      // `gateCommandByOperation` call at the end of this branch actually authorize
      // it against `sales-channels:integrations:configure`. Removing that entry
      // without removing this allow silently un-gates it.
      if (
        type.startsWith( 'integrations/erps/drift/' ) &&
        type !== 'integrations/erps/drift/setFilter' &&
        type !== 'integrations/erps/drift/setPage' &&
        type !== 'integrations/erps/drift/recheck/request'
      ) {
        return false;
      }
      return gateCommandByOperation( commandOperations, { state, command, meta });
    }

    return false;
  },

  normalize: ( state ) => {
    let normalized = { ...state };

    if (
      normalized.erpDetail.mode.type === 'view' &&
      !normalized.erps[normalized.erpDetail.mode.erpId]
    ) {
      normalized = {
        ...normalized,
        erpDetail: {
          mode: { type: 'closed' },
          // The selected tab is view state, not entity state: it survives the
          // integration going missing so a re-open (or a `?tab=` restore that
          // ran before the snapshot landed) is not silently discarded.
          activeTab: normalized.erpDetail.activeTab,
          isEditing: false,
          formData: normalized.erpDetail.formData,
          formErrors: {}
        }
      };
    }

    // The drift panel is a VIEW of the open detail. Once the detail is not in
    // `view` mode the rows belong to an integration that is no longer on screen,
    // so they are dropped rather than left to be shown against the next one.
    // Fresh objects, never aliases of the exported defaults or of
    // `initialState.driftAudit`, so shared module state can never be mutated.
    const driftIsAlreadyEmpty =
      normalized.driftAudit.rows.length === 0 &&
      normalized.driftAudit.total === 0 &&
      normalized.driftAudit.rechecking.length === 0 &&
      Object.keys( normalized.driftAudit.recheckOutcomes ).length === 0 &&
      normalized.driftAudit.page === 1 &&
      !normalized.driftAudit.loading;

    // The guard keeps `normalize` a no-op once the slice is empty, so the
    // reference stays stable and a policy's `shouldReact` cannot see a phantom
    // change on every unrelated dispatch while the detail is closed.
    if ( normalized.erpDetail.mode.type !== 'view' && !driftIsAlreadyEmpty ) {
      normalized = {
        ...normalized,
        driftAudit: {
          rows: [],
          total: 0,
          filter: { ...DEFAULT_DRIFT_FILTER },
          page: 1,
          loading: false,
          rechecking: [],
          recheckOutcomes: {}
        }
      };
    }

    return normalized;
  },

  validate: ( state ) => {
    if ( state.addERPModal.isOpen && state.addERPModal.step === 'enterDetails' ) {
      const errors: Record<string, string> = {};
      const { formData } = state.addERPModal;

      if ( !formData.name.trim()) {
        errors.name = 'Name is required';
      }

      return Object.keys( errors ).length > 0 ? errors : null;
    }

    return null;
  }
};
